import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import API_BASE from './apiBase';
import './toast';
import '../css/App.css';
import DataBreachForm from './DataBreachesForm';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import SmartNotices from './SmartNotices';
import { createSessionCacheKey, readSessionCache, writeSessionCache } from './sessionDataCache';
import { formatCompactNumber } from './numberFormatUtils';
import maskUtils from './maskUtils';

const { maskEmail, maskPhone } = maskUtils;

/* ═══════════════════════════════════════════════════════════════
   CONSTANTS
═══════════════════════════════════════════════════════════════ */
const ROLES = ['Employee', 'Vendor', 'Partners', 'Customer'];

const ROLE_META = {
  Employee: { icon: '👤', label: 'Employees' },
  Vendor: { icon: '🏢', label: 'Vendors & Suppliers' },
  Partners: { icon: '🤝', label: 'Partners' },
  Customer: { icon: '🛍️', label: 'Customers' },
};

const STATUS_BADGE_CLS = {
  accepted: 'np-badge--accepted',
  declined: 'np-badge--declined',
  revoked: 'np-badge--revoked',
  unknown: 'np-badge--unknown',
};

const STAT_BAR_COLORS = {
  total: '#1e3a7a',
  accepted: '#2e5c0e',
  declined: '#8c1f1f',
  revoked: '#5a5855',
};
const GUIDE_STEPS = [
  { title: 'Notices', body: 'This page shows all Privacy Notices along with their consent records.', target: '.np-main' },
  { title: 'Sidebar', body: 'The left sidebar lists all Privacy Notices. Click any notice to load its consent statistics.', target: '#tour-sidebar' },
  { title: 'View Notice Link', body: 'Clicking "View notice ↗" opens the original notice in a new tab.', target: '#tour-view-notice' },
  { title: 'Smart Notices', body: 'Click the + button to open Smart Notices, allowing targeted notice dispatch to accepted recipients by location, department, and group.', target: '[data-tour="smart-notices-btn"]' },
  { title: 'Sent Counter', body: 'This counter shows the total number of emails sent successfully so far. It updates automatically after every new send.', target: '[data-tour="sent-counter"]' },
  { title: 'Data Breaches Button', body: 'Clicking the 🔔 Data Breaches button opens the breach templates page, from where you can also send breach notices.', target: '[data-tour="data-breaches-btn"]' },
  { title: 'Stat Cards', body: 'Click on the Total / Accepted / Declined / Revoked cards to filter the table by that status. Click again to remove the filter.', target: '#tour-stats-grid' },
  { title: 'Role Cards', body: 'Click the Employee / Vendor / Partners card to show only records for that role in the table.', target: '#tour-role-grid' },
  { title: 'Select All Checkbox', body: 'The checkbox in the table header selects all visible rows at once.', target: '[data-tour="select-all-checkbox"]' },
  { title: 'Row Checkbox', body: 'Each row checkbox lets you select individual users for sending bulk emails.', target: '.np-checkbox' },
  { title: "User Name Link", body: "Clicking a user's name opens a modal with their full details (email, phone, status, consent preview).", target: '[data-tour="user-name-link"]' },
  { title: 'Email Selected', body: 'This button sends emails only to the users whose checkboxes are ticked. It stays disabled until at least one row is selected.', target: '[data-tour="email-selected-btn"]' },
  { title: 'Clear Button', body: 'The Clear button deselects all currently selected rows.', target: '[data-tour="clear-selection-btn"]' },
  { title: 'Email All', body: 'The ⚡ Email All button sends emails to all rows currently visible under the active filter (after a confirmation popup).', target: '[data-tour="email-all-btn"]' },
  { title: 'Row Click / Preview', body: "Clicking any table row opens a preview panel on the side showing the notice's email template.", target: '.np-tr' },
  { title: 'Send Email (Preview)', body: 'The "✉ Send Email" button inside the preview panel sends the email only to that selected user.', target: '[data-tour="preview-send-btn"]' },
  { title: 'Close Preview', body: 'The "✕ Close" button closes the preview panel.', target: '[data-tour="preview-close-btn"]' },
  { title: 'Send Email (User Details)', body: 'The Send Email button inside the User Details modal sends the notice email only to that specific user and then closes the modal.', target: '[data-tour="userdetails-send-btn"]' },
];


/* ═══════════════════════════════════════════════════════════════
   PURE HELPERS
═══════════════════════════════════════════════════════════════ */
export function normaliseStatus(r) {
  if (!r) return null;

  // 1) Check common boolean/numeric flags first (these often come as booleans, not strings)
  const boolFields = [
    'is_accepted', 'accepted', 'is_agreed', 'agreed',
    'is_declined', 'declined', 'is_rejected', 'rejected',
    'is_revoked', 'revoked',
  ];
  for (const f of boolFields) {
    if (r[f] === true || r[f] === 1 || r[f] === '1') {
      if (f.includes('declin') || f.includes('reject')) return 'declined';
      if (f.includes('revok')) return 'revoked';
      if (f.includes('accept') || f.includes('agree')) return 'accepted';
    }
  }

  // 2) Fallback to string-based fields (added more possible keys)
  const raw = (
    r.status ??
    r.consent_status ??
    r.state ??
    // r.consent ??
    r.response ??
    r.response_status ??
    r.consent_response ??
    r.action ??
    r.result ??
    r.answer ??
    ''
  );

  // Handle numeric/boolean values that slipped through as raw
  if (raw === true || raw === 1 || raw === '1') return 'accepted';
  if (raw === false || raw === 0 || raw === '0') return 'declined';

  const v = String(raw).trim().toLowerCase();
  if (!v) return null;

  // Order matters: check revoked / declined before accepted so 'accepted then revoked' counts as revoked.
  if (v.includes('revok')) return 'revoked';
  if (v.includes('declin') || v.includes('reject') || v.includes('deny') || v.includes('denied') || v === 'no' || v === 'false') {
    return 'declined';
  }
  if (
    v.includes('accept') ||
    v.includes('agree') ||
    // v.includes('consent') ||
    v.includes('opt_in') ||
    v.includes('opted_in') ||
    v.includes('opted-in') ||
    v.includes('subscribe') ||
    v.includes('confirm') ||
    v === 'active' ||
    v === 'approved' ||
    v === 'granted' ||
    v === 'yes' ||
    v === 'true' ||
    v === 'signed' ||
    v === 'ok' ||
    v === 'success'
  ) {
    return 'accepted';
  }

  return null;
}

export function normaliseRole(r) {
  const combined = [r.role, r.user_role, r.type, r.consent_type, r.category, r.template, r.entity]
    .filter(Boolean).join(' ').toLowerCase();
  if (!combined) return null;
  if (['employee', 'staff', 'member', 'employeeid', 'emp'].some((k) => combined.includes(k))) return 'Employee';
  if (['vendor', 'supplier', 'vendorid', 'vendorcompany'].some((k) => combined.includes(k))) return 'Vendor';
  if (['partner', 'partnerid', 'affiliate'].some((k) => combined.includes(k))) return 'Partners';
  if (['customer', 'client', 'buyer', 'consumer'].some((k) => combined.includes(k))) return 'Customer';
  return null;
}

export function consentKey(r) {
  const email = (r.email || '').trim().toLowerCase();
  const phone = (r.phone || '').replace(/[\s\-+]/g, '');
  const username = (r.username || r.name || '').trim().toLowerCase();
  const uid = (r.user_id || r.userId || '').toString();
  const identity = email || phone || username || uid || 'anon';
  const type = (r.type || r.consent_type || r.category || r.template || '').trim().toLowerCase();
  return `${identity}||${type}`;
}

export function noticeToken(notice) {
  return (notice.consent_type || notice.title || notice.card_data?.label || '').toLowerCase();
}

export function matchesNotice(consentType, token) {
  if (!token || !consentType) return false;
  return consentType === token || consentType.includes(token) || token.includes(consentType);
}

function emptyStats() {
  return { total: 0, accepted: 0, declined: 0, revoked: 0 };
}

function displayName(r) {
  return r.email || r.username || r.name || r.user_id || '—';
}

function formatDate(val) {
  if (!val) return '—';
  const d = new Date(val);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

function displayLanguage(record) {
  const value = record?.consent_language
    || record?.preferred_languages
    || record?.language
    || record?.language_code;
  if (!value) return '—';
  return String(value)
    .split(',')[0]
    .trim()
    .toUpperCase();
}

function stripHtmlTags(html) {
  if (!html) return '';
  // Remove HTML tags and decode entities
  return String(html)
    .replace(/<[^>]*>/g, '')  // Remove all HTML tags
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/&amp;/g, '&')
    .trim();
}

/* ═══════════════════════════════════════════════════════════════
   DATA LAYER
═══════════════════════════════════════════════════════════════ */
export function buildNoticeStats(notices, consents) {
  const mapped = notices.map((n) => ({
    notice: n,
    roles: { Employee: emptyStats(), Vendor: emptyStats(), Partners: emptyStats(), Customer: emptyStats() },
  }));

  for (const r of consents) {
    // const key = consentKey(r);
    // if (seen.has(key)) continue;
    // seen.add(key);
    const type = (r.type || r.consent_type || r.category || r.template || '').toLowerCase();
    const role = normaliseRole(r) || 'Employee';
    const st = normaliseStatus(r);

    for (const m of mapped) {
      if (!matchesNotice(type, noticeToken(m.notice))) continue;
      m.roles[role].total++;
      if (st) m.roles[role][st]++;
      break;
    }
  }

  return mapped.sort((a, b) => {
    const aT = String(a.notice.title || a.notice.name || a.notice.public_id || '').toLowerCase();
    const bT = String(b.notice.title || b.notice.name || b.notice.public_id || '').toLowerCase();
    return aT.localeCompare(bT, undefined, { sensitivity: 'base' });
  });
}

/* ═══════════════════════════════════════════════════════════════
   API LAYER
═══════════════════════════════════════════════════════════════ */
export async function fetchNoticePdf(noticeId, titleRaw) {
  try {
    const res = await axios.get(`${API_BASE}/user/privacy-notices/${noticeId}/pdf`, {
      responseType: 'blob',
    });
    if (!res?.data) return null;
    const filename = (titleRaw || 'privacy_notice').replace(/[^a-z0-9_\-.]/gi, '_') + '.pdf';
    try {
      return { blob: new File([res.data], filename, { type: 'application/pdf' }), filename };
    } catch {
      return { blob: res.data, filename };
    }
  } catch (e) {
    console.warn('[NoticesPage] PDF fetch failed', e);
    return null;
  }
}

function buildFormData(raw, noticeMeta, template, pdf, selectedDrivePreference) {
  const form = new FormData();
  if (raw.public_id) form.append('public_id', String(raw.public_id));
  else if (raw.id) form.append('consent_id', String(raw.id));
  if (noticeMeta?.id) form.append('privacy_notice_id', String(noticeMeta.id));
  form.append('include_response_links', 'false');

  const userLang = raw?.consent_language || raw?.preferred_languages || raw?.language || raw?.language_code;
  if (userLang) {
    form.append('target_language', String(userLang).trim());
    form.append('translate', 'true');
  }

  if (template) form.append('message', template);
  if (pdf) form.append('attachments', pdf.blob, pdf.filename);
  if (selectedDrivePreference) {
    form.append('drive_type', selectedDrivePreference);
  }
  return form;
}

export async function sendEmails(rawRecords, selectedNotice) {
  const noticeMeta = selectedNotice?.notice;
  const noticeId = noticeMeta?.id || noticeMeta?.public_id;
  const template = noticeMeta ? (noticeMeta.html || noticeMeta.body || noticeMeta.text || '') : '';
  const token = localStorage.getItem('token') || '';
  const selectedDrivePreference = (() => {
    try {
      return (window && window.localStorage && window.localStorage.getItem('consent_selected_drive')) || '';
    } catch (e) {
      return '';
    }
  })();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  if (selectedDrivePreference) {
    headers['X-Drive-Type'] = selectedDrivePreference;
  }

  const pdf = noticeId ? await fetchNoticePdf(noticeId, noticeMeta?.title) : null;

  const promises = rawRecords.map((raw) => {
    const form = buildFormData(raw, noticeMeta, template, pdf, selectedDrivePreference);
    const url = raw.public_id
      ? `${API_BASE}/user/consent/resend_by_public`
      : `${API_BASE}/user/consent/${raw.id}/resend`;
    return axios.post(url, form, { headers });
  });

  const settled = await Promise.allSettled(promises);
  let success = 0, fail = 0;
  const errors = [];
  for (const s of settled) {
    if (s.status === 'fulfilled') success++;
    else { fail++; errors.push(s.reason?.message || 'unknown'); }
  }
  return { success, fail, errors };
}

/* ═══════════════════════════════════════════════════════════════
   SUB-COMPONENTS
═══════════════════════════════════════════════════════════════ */

/* Sent counter pill — flashes on increment */
/* Page Guide modal */
function GuideModal({ steps, onClose }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 2000,
        background: 'rgba(15,23,42,0.55)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '32px 16px', overflowY: 'auto',
      }}
    >
      <div style={{
        background: '#fff', borderRadius: 16, width: '100%', maxWidth: 620,
        border: '1px solid #e2e8f0', boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid #f1f5f9' }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a' }}>📖 Page Guide</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 8, color: '#94a3b8', fontSize: 18, lineHeight: 1 }}>✕</button>
        </div>
        <div style={{ padding: '18px 20px', maxHeight: '75vh', overflowY: 'auto' }}>
          {steps.map((s, i) => (
            <div key={i} style={{
              display: 'flex', gap: 12, padding: '12px 0',
              borderBottom: i < steps.length - 1 ? '1px solid #f1f5f9' : 'none',
            }}>
              <div style={{
                flexShrink: 0, width: 26, height: 26, borderRadius: '50%',
                background: '#eff6ff', color: '#1e3a7a', fontWeight: 700, fontSize: 12,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>{i + 1}</div>
              <div>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: '#0f172a', marginBottom: 3 }}>{s.title}</div>
                <div style={{ fontSize: 12.5, color: '#4b5563', lineHeight: 1.5 }}>{s.body}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
function SentCounter({ count }) {
  const prevRef = useRef(count);
  const [flash, setFlash] = useState(false);

  useEffect(() => {
    if (count !== prevRef.current) {
      prevRef.current = count;
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 600);
      return () => clearTimeout(t);
    }
  }, [count]);

  return (
    <div className={`np-sent-pill${flash ? ' np-sent-pill--flash' : ''}`} data-tour="sent-counter">
      <span className="np-sent-dot" aria-hidden="true" />
      <span className="np-sent-label">Notices Sent</span>
      <span className="np-sent-count" aria-live="polite" title={Number(count || 0).toLocaleString()}>{formatCompactNumber(count)}</span>
    </div>
  );
}

/* Status badge */
function StatusBadge({ status }) {
  const cls = STATUS_BADGE_CLS[status] || STATUS_BADGE_CLS.unknown;
  const label = status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown';
  return <span className={`np-badge ${cls}`}>{label}</span>;
}

/* Stat card */
function StatCard({ filterKey, label, value, total, activeKey, onFilter }) {
  const pct = total ? Math.round((value / total) * 100) : 0;
  const isAct = activeKey === filterKey || (!activeKey && filterKey === 'total');

  return (
    <div
      className={`np-stat-card${isAct ? ' np-stat-card--active' : ''}`}
      data-tour="stat-card"
      role="button"
      tabIndex={0}
      onClick={() => onFilter(filterKey)}
      onKeyDown={(e) => e.key === 'Enter' && onFilter(filterKey)}
      aria-pressed={isAct}
    >
      <div className="np-stat-label">{label}</div>
      <div className="np-stat-num" title={Number(value || 0).toLocaleString()}>{formatCompactNumber(value)}</div>
      <div className="np-stat-bar">
        <div
          className="np-stat-bar-fill"
          style={{ width: `${pct}%`, background: STAT_BAR_COLORS[filterKey] }}
        />
      </div>
    </div>
  );
}

/* Role card */
function RoleCard({ role, stats, isActive, onClick }) {
  const meta = ROLE_META[role];
  return (
    <div
      className={`np-role-card${isActive ? ' np-role-card--active' : ''}`}
      data-tour="role-card"
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
      aria-pressed={isActive}
    >
      <div className="np-role-card-head">
        <div className="np-role-icon-wrap">
          <div className="np-role-icon-box" aria-hidden="true">{meta.icon}</div>
          <span className="np-role-name">{meta.label}</span>
        </div>
        <span className="np-role-total" title={Number(stats.total || 0).toLocaleString()}>{formatCompactNumber(stats.total)}</span>
      </div>
      <div className="np-role-mini-stats">
        {['accepted', 'declined', 'revoked'].map((k) => (
          <span key={k} className="np-mini-stat">
            <span className={`np-mini-dot np-mini-dot--${k}`} aria-hidden="true" />
            <span className="np-mini-val" title={Number(stats[k] || 0).toLocaleString()}>{formatCompactNumber(stats[k])}</span>
            <span className="np-mini-lbl">{k.slice(0, 3)}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/* Preview panel */
function PreviewPanel({ row, selectedNotice, sending, sendResult, onSend, onClose }) {
  if (!row) return null;

  const tpl = selectedNotice?.notice
    ? (selectedNotice.notice.html || selectedNotice.notice.body || selectedNotice.notice.text || '')
    : '';

  const rendered = tpl
    ? tpl.replace(/{{\s*(email|name|username)\s*}}/gi, (_, k) => {
      const lk = k.toLowerCase();
      if (lk === 'email') return row.email || '';
      if (lk === 'name') return row.name || row.username || '';
      if (lk === 'username') return row.username || row.name || '';
      return '';
    })
    : null;

  // Append declaration signature (if present on the selected notice) to the preview HTML
  let renderedWithSig = rendered;
  try {
    const sig = selectedNotice?.notice?.declaration_signature || selectedNotice?.notice?.declarationSignature || '';
    if (sig) {
      const sigHtml = `<hr/><p style="font-size:12px;color:#444">Declaration signature:<br/><em>${sig}</em></p>`;
      renderedWithSig = (renderedWithSig || '') + sigHtml;
    }
  } catch (e) {
    renderedWithSig = rendered;
  }

  return (
    <div className="np-preview-panel">
      <div className="np-preview-header">
        <div className="np-preview-user">
          <div className="np-avatar np-avatar--lg" aria-hidden="true">
            {displayName(row)[0].toUpperCase()}
          </div>
          <div>
            <p className="np-preview-user-name">{displayName(row)}</p>
            <p className="np-preview-user-meta">
              {row.type || row.consent_type || '—'} · {formatDate(row.created_at || row.createdAt)}
            </p>
          </div>
        </div>
        <div className="np-preview-actions">
          <button className="np-btn np-btn--primary" data-tour="preview-send-btn" disabled={sending} onClick={onSend}>
            {sending ? '⏳ Sending…' : '✉ Send Email'}
          </button>
          <button className="np-btn np-btn--ghost" data-tour="preview-close-btn" onClick={onClose}>✕ Close</button>
        </div>
      </div>

      {/* Consent details (show the consent column/ message for this row) */}
      {(row.consent || row.consent_message || row.message || row.content) && (
        <div className="np-consent-details" style={{ padding: '12px 16px', borderTop: '1px solid #f1f5f9', background: '#fbfcfe' }}>
          <div style={{ fontSize: 13, color: '#475569', marginBottom: 6 }}>Consent Details</div>
          {/* <div style={{ fontSize: 14, color: '#0f172a', maxHeight: '18vh', overflowY: 'auto', whiteSpace: 'pre-wrap' }}>{row.consent || row.consent_message || row.message || row.content}</div> */}
          <div style={{ fontSize: 14, color: '#0f172a', maxHeight: '18vh', overflowY: 'auto', whiteSpace: 'pre-wrap' }}>{stripHtmlTags(row.consent || row.consent_message || row.message || row.content)}</div>
        </div>
      )}

      {sendResult && (
        <div className={`np-send-result ${sendResult.success ? 'np-send-result--success' : 'np-send-result--error'}`}>
          {sendResult.success
            ? '✓ Email sent successfully'
            : `✗ Failed: ${String(sendResult.error)}`}
        </div>
      )}

      <div className="np-preview-body" style={{ maxHeight: '48vh', overflowY: 'auto', padding: '12px 16px' }}>
        {renderedWithSig
          ? <div dangerouslySetInnerHTML={{ __html: renderedWithSig }} />
          : <p className="np-preview-empty">No preview content available for this notice.</p>
        }
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MAIN PAGE COMPONENT
═══════════════════════════════════════════════════════════════ */
export default function NoticesPage({ user, onMenu }) {
  /* ── Data ── */
  const cacheKey = createSessionCacheKey('notices', user, { tenantId: user?.tenant_id });
  const [cachedData] = useState(() => readSessionCache(cacheKey));
  const [noticeData, setNoticeData] = useState(() => cachedData?.noticeData || []);
  const [consents, setConsents] = useState(() => cachedData?.consents || []);
  const [loading, setLoading] = useState(() => !cachedData);
  const [error, setError] = useState(null);

  /* ── UI ── */
  const [selectedId, setSelectedId] = useState(() => {
    const firstNotice = cachedData?.noticeData?.[0]?.notice;
    return firstNotice ? String(firstNotice.id || firstNotice.public_id || '') : '';
  });
  const [filterKey, setFilterKey] = useState(null);
  const [filterRole, setFilterRole] = useState(null);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [guideOpen, setGuideOpen] = useState(false);
  const [showSmartNotices, setShowSmartNotices] = useState(false);

  /* ── Preview ── */
  const [previewRow, setPreviewRow] = useState(null);
  const [sendResult, setSendResult] = useState(null);
  const [userDetailsRow, setUserDetailsRow] = useState(null);
  const [breachesOpen, setBreachesOpen] = useState(false);
  const [breachTemplates, setBreachTemplates] = useState([]);
  const [showBreachPage, setShowBreachPage] = useState(false);
  const [breachCount, setBreachCount] = useState(0);

  // Replace simple placeholders for preview/send: {{name}}, {{email}}, [date of breach], [Date]
  function applyPreviewPlaceholders(text, rec) {
    try {
      if (!text) return '';
      let out = String(text);
      const name = (rec && (rec.username || rec.name || rec.email)) || '';
      const email = (rec && (rec.email || rec.username)) || '';
      const today = new Date().toISOString().slice(0, 10);
      out = out.replace(/{{\s*name\s*}}/gi, name);
      out = out.replace(/{{\s*username\s*}}/gi, name);
      out = out.replace(/{{\s*email\s*}}/gi, email);
      out = out.replace(/\[date of breach\]/gi, today);
      out = out.replace(/\[Date\]|\[date\]/gi, today);
      return out;
    } catch (e) { return text || ''; }
  }

  /* ── Send ── */
  const [sending, setSending] = useState(false);
  const [sentTotal, setSentTotal] = useState(0);
  // persist sent counter per-tenant for 7 days
  const _sentStorageKey = (() => {
    try {
      const tid = user?.tenant_id || Number(localStorage.getItem('tenant_id')) || 0;
      return `np_sent_total_${tid}`;
    } catch (e) { return 'np_sent_total_0'; }
  })();

  // load persisted count (if within TTL)
  useEffect(() => {
    try {
      const raw = localStorage.getItem(_sentStorageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed) return;
      const { count, ts } = parsed;
      if (!ts || !count) return;
      const age = Date.now() - Number(ts || 0);
      const TTL = 7 * 24 * 60 * 60 * 1000; // 7 days
      if (age <= TTL) {
        setSentTotal(Number(count) || 0);
      } else {
        localStorage.removeItem(_sentStorageKey);
      }
    } catch (e) { /* ignore */ }
  }, [_sentStorageKey]);

  // persist count on change
  useEffect(() => {
    try {
      const payload = { count: Number(sentTotal) || 0, ts: Date.now() };
      localStorage.setItem(_sentStorageKey, JSON.stringify(payload));
    } catch (e) { /* ignore */ }
  }, [sentTotal, _sentStorageKey]);

  /* ── Derived ── */
  const selectedNotice = useMemo(
    () => noticeData.find((m) => String(m.notice.id || m.notice.public_id) === selectedId) ?? null,
    [noticeData, selectedId],
  );

  const filteredRows = useMemo(() => {
    if (!selectedNotice) return [];
    const token = noticeToken(selectedNotice.notice);
    const seen = new Set();
    return consents.filter((r) => {
      const key = consentKey(r);
      if (seen.has(key)) return false;
      seen.add(key);
      const type = (r.type || r.consent_type || r.category || r.template || '').toLowerCase();
      if (!matchesNotice(type, token)) return false;
      if (filterRole && (normaliseRole(r) || 'Employee') !== filterRole) return false;
      const st = normaliseStatus(r) || 'unknown';
      // Only show Accepted records in the table (Declined/Revoked/Unknown are hidden)
      if (st !== 'accepted') return false;
      if (filterKey && st !== filterKey) return false;
      return true;
    });
  }, [selectedNotice, consents, filterRole, filterKey]);

  const statCounts = useMemo(() => {
    const counts = { total: filteredRows.length, accepted: 0, declined: 0, revoked: 0 };
    filteredRows.forEach((r) => { const st = normaliseStatus(r); if (st) counts[st]++; });
    return counts;
  }, [filteredRows]);

  const allSelected = filteredRows.length > 0
    && filteredRows.every((r) => selectedRows.has(String(r.id || r.public_id)));

  /* ── Load ── */
  useEffect(() => {
    let mounted = true;
    const cached = readSessionCache(cacheKey);
    setError(null);
    setLoading(!cached);
    if (cached) {
      setNoticeData(cached.noticeData || []);
      setConsents(cached.consents || []);
    }
    (async () => {
      try {
        const [noticesRes, consentsRes] = await Promise.all([
          axios.get(`${API_BASE}/user/privacy-notices`, { params: { tenant_id: user?.tenant_id }, skipLoading: true }),
          axios.get(`${API_BASE}/user/consent`, { params: { tenant_id: user?.tenant_id }, skipLoading: true }),
        ]);
        if (!mounted) return;
        const notices = noticesRes?.data?.privacy_notices ?? [];
        const cList = consentsRes?.data?.consents ?? [];
        const built = buildNoticeStats(notices, cList);
        setNoticeData(built);
        setConsents(cList);
        writeSessionCache(cacheKey, { noticeData: built, consents: cList });
        if (built.length) setSelectedId(String(built[0].notice.id || built[0].notice.public_id || ''));
      } catch {
        if (mounted) setError('Failed to load notices. Please try again.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [user, cacheKey]);

  // fetch count of data_breaches for this tenant to show on topbar
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const tenant_id = user?.tenant_id || Number(localStorage.getItem('tenant_id')) || null;
        const params = tenant_id ? { tenant_id } : {};
        const res = await axios.get(`${API_BASE}/manager/data-breaches/count`, { params });
        if (!mounted) return;
        const cnt = res?.data?.count || 0;
        setBreachCount(Number(cnt));
      } catch (e) {
        console.warn('Failed to fetch breach count', e);
      }
    })();
    return () => { mounted = false; };
  }, [user]);

  // Lock body scroll only when the modal-style breaches overlay is open (`breachesOpen`) —
  // allow embedded `showBreachPage` to scroll internally.
  useEffect(() => {
    const prev = document.body.style.overflow;
    if (breachesOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = prev;
    return () => { document.body.style.overflow = prev; };
  }, [breachesOpen]);

  /* Reset on notice change */
  useEffect(() => {
    setSelectedRows(new Set());
    setSendResult(null);
    setPreviewRow(null);
  }, [selectedId]);

  /* ── Handlers ── */
  const handleSelectNotice = useCallback((id) => {
    setSelectedId(id);
    setFilterKey(null);
    setFilterRole(null);
  }, []);

  const handleFilterStatus = useCallback((key) => {
    setFilterKey((prev) => (prev === key || key === 'total') ? null : key);
    setSelectedRows(new Set());
  }, []);

  const handleFilterRole = useCallback((role) => {
    setFilterRole((prev) => prev === role ? null : role);
    setSelectedRows(new Set());
  }, []);

  const handleToggleRow = useCallback((id) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const handleToggleAll = useCallback(() => {
    setSelectedRows(allSelected
      ? new Set()
      : new Set(filteredRows.map((r) => String(r.id || r.public_id))),
    );
  }, [allSelected, filteredRows]);

  /* Shared send logic */
  const executeSend = useCallback(async (rows) => {
    if (!rows.length) return;
    const conf = await Swal.fire({
      title: 'Send Email',
      text: `Send email to ${rows.length} recipient${rows.length !== 1 ? 's' : ''}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Send',
      cancelButtonText: 'Cancel',
      customClass: { popup: 'swal-popup-custom', confirmButton: 'swal-confirm-btn', cancelButton: 'swal-cancel-btn' },
      buttonsStyling: false
    });
    if (!conf.isConfirmed) return;
    setSending(true);
    setSendResult(null);
    try {
      const result = await sendEmails(rows, selectedNotice);
      setSentTotal((prev) => prev + result.success);
      setSendResult({ successCount: result.success, failCount: result.fail, errors: result.errors });
      try { window.toastSuccess(`Sent: ${result.success} · Failed: ${result.fail}`); } catch (e) { Swal.fire({ icon: 'success', title: 'Send Result', text: `Sent: ${result.success}  Failed: ${result.fail}`, customClass: { popup: 'swal-popup-custom' } }); }
    } catch (err) {
      setSendResult({ successCount: 0, failCount: 1, errors: [err.message ?? String(err)] });
      try { window.toastError('Send failed. Please try again.'); } catch (e) { Swal.fire({ icon: 'error', title: 'Send Failed', text: 'Send failed. Please try again.', customClass: { popup: 'swal-popup-custom' } }); }
    } finally {
      setSending(false);
    }
  }, [selectedNotice]);

  const handleEmailAll = useCallback(
    () => executeSend(filteredRows),
    [executeSend, filteredRows],
  );

  const handleEmailSelected = useCallback(() => {
    const rows = filteredRows.filter((r) => selectedRows.has(String(r.id || r.public_id)));
    executeSend(rows);
  }, [executeSend, filteredRows, selectedRows]);

  const handleSingleSend = useCallback(async () => {
    if (!previewRow) return;
    const singleConf = await Swal.fire({
      title: 'Send Email',
      text: 'Send email to this recipient?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Send',
      cancelButtonText: 'Cancel',
      customClass: { popup: 'swal-popup-custom', confirmButton: 'swal-confirm-btn', cancelButton: 'swal-cancel-btn' },
      buttonsStyling: false
    });
    if (!singleConf.isConfirmed) return;
    setSending(true);
    setSendResult(null);
    try {
      const result = await sendEmails([previewRow], selectedNotice);
      setSentTotal((prev) => prev + result.success);
      setSendResult({ success: result.success > 0, error: result.errors[0] });
      if (result.success > 0) try { window.toastSuccess('Email sent'); } catch (e) { Swal.fire({ icon: 'success', title: 'Email Sent', text: 'Email sent', customClass: { popup: 'swal-popup-custom' } }); }
      else try { window.toastError('Send failed'); } catch (e) { Swal.fire({ icon: 'error', title: 'Send Failed', text: 'Send failed', customClass: { popup: 'swal-popup-custom' } }); }
    } catch (err) {
      setSendResult({ success: false, error: err?.response?.data ?? err.message ?? String(err) });
      try { window.toastError('Failed to send email'); } catch (e) { Swal.fire({ icon: 'error', title: 'Send Failed', text: 'Failed to send email', customClass: { popup: 'swal-popup-custom' } }); }
    } finally {
      setSending(false);
    }
  }, [previewRow, selectedNotice]);

  const selectedCount = selectedRows.size;

  const fetchBreachesData = useCallback(async () => {
    try {
      const tenant_id =
        user?.tenant_id ||
        Number(localStorage.getItem('tenant_id')) ||
        null;

      const token =
        localStorage.getItem('token') ||
        sessionStorage.getItem('token');

      const apiKey =
        localStorage.getItem('api_key');

      const params = tenant_id ? { tenant_id } : {};

      const headers = {
        Authorization: `Bearer ${token}`,
        'x-api-key': apiKey,
        'Content-Type': 'application/json'
      };

      const res = await axios.get(
        `${API_BASE}/manager/data-breaches`,
        {
          params,
          headers
        }
      );

      const breaches = res?.data?.breaches || [];

      setBreachTemplates(breaches);
      setBreachCount(breaches.length);

      // popup open
      setBreachesOpen(true);

    } catch (err) {
      console.error('Failed to fetch breaches', err);

      // 401 par logout mat karo
      if (err?.response?.status === 401) {
        try {
          window.toastError('Unauthorized access');
        } catch {
          Swal.fire({ icon: 'error', title: 'Unauthorized', text: 'Unauthorized access', customClass: { popup: 'swal-popup-custom' } });
        }
        return;
      }

      try {
        window.toastError('Failed to load data breaches');
      } catch {
        Swal.fire({ icon: 'error', title: 'Load Failed', text: 'Failed to load data breaches', customClass: { popup: 'swal-popup-custom' } });
      }
    }
  }, [user]);


  const handleOpenSmartNotices = useCallback(() => {
    if (onMenu) {
      onMenu('smart-notices');
      try {
        window.history.pushState({}, '', '/user/smart-notices');
        window.dispatchEvent(new PopStateEvent('popstate'));
      } catch (_) { }
    } else {
      setShowSmartNotices(true);
    }
  }, [onMenu]);

  /* ─────────────── RENDER ─────────────── */
  if (showSmartNotices) {
    return (
      <SmartNotices
        user={user}
        onBack={() => setShowSmartNotices(false)}
        onMenu={onMenu}
      />
    );
  }

  if (loading) {
    return (
      <div className="np-page">
        <div className="np-loading">
          <div className="np-spinner" />
          <p>Loading notices…</p>
        </div>
      </div>
    );
  }

  if (error && !cachedData) {
    return (
      <div className="np-page">
        <div className="np-error-banner" role="alert">{error}</div>
      </div>
    );
  }

  return (
    <div className="np-page">
      {error && <div className="np-error-banner" role="alert">{error}</div>}

      {/* ══ Topbar ══════════════════════════════════════════ */}
      <header className="np-topbar">
        <div className="np-topbar-left">
          <div className="np-logo" aria-hidden="true">N</div>
          <span className="np-topbar-title">Notices</span>

        </div>
        <div className="np-topbar-right">
          {/* Smart Notice (+) button before Notices Sent */}
          <button
            type="button"
            className="np-btn np-btn--ghost np-smart-notices-btn"
            data-tour="smart-notices-btn"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: '50%',
              padding: 0,
              background: '#f0fdf4',
              border: '1px solid rgba(52, 211, 153, 0.45)',
              color: '#059669',
              cursor: 'pointer',
              transition: 'all 0.18s ease',
            }}
            onClick={handleOpenSmartNotices}
            title="Smart Notices"
            aria-label="Smart Notices"
          >
            <FontAwesomeIcon icon={faPlus} style={{ color: '#059669', fontSize: 13 }} />
          </button>

          <SentCounter count={sentTotal} />

          {/*  <button
        className="np-btn np-btn--ghost"
        style={{
          marginLeft: 12,
          padding: '6px 10px',
          fontSize: 13,
          position: 'relative'
        }}
        onClick={fetchBreachesData}
        title="Open Data Breaches"
      >
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          🔔
        </span>

        {breachCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: -6,
              right: -6,
              background: '#ef4444',
              color: '#fff',
              borderRadius: 10,
              padding: '2px 6px',
              fontSize: 12
            }}
          >
            {breachCount}
          </span>
        )}
      </button> */}
          <button
            className="np-btn np-btn--ghost"
            data-tour="page-guide-btn"
            style={{ marginLeft: 14, padding: '6px 12px', fontSize: 13, fontWeight: 600 }}
            onClick={() => setGuideOpen(true)}
          >
            📖 Page Guide
          </button>
        </div>
      </header>

      {/* ══ Body ════════════════════════════════════════════ */}
      <div className="np-layout">

        {/* Sidebar */}
        {/* <aside className="np-sidebar" aria-label="Privacy notices"> */}
        <aside className="np-sidebar" id="tour-sidebar" aria-label="Privacy notices">
          <div className="np-sidebar-label">Privacy Notices</div>
          {noticeData.map((m) => {
            const id = String(m.notice.id || m.notice.public_id);
            const title = m.notice.title || m.notice.name || m.notice.public_id;
            const total = ROLES.reduce((s, r) => s + (m.roles[r]?.total || 0), 0);
            const isAct = id === selectedId;
            return (
              <div
                key={id}
                className={`np-notice-item${isAct ? ' active' : ''}`}
                data-tour="notice-item"
                role="button"
                tabIndex={0}
                onClick={() => handleSelectNotice(id)}
                onKeyDown={(e) => e.key === 'Enter' && handleSelectNotice(id)}
                aria-current={isAct ? 'page' : undefined}
              >
                <div className="np-notice-item-name">{title}</div>
                <div className="np-notice-item-sub">{total.toLocaleString()} records</div>
              </div>
            );
          })}
        </aside>

        {/* Main */}
        <main className="np-main">

          {/* Page header */}
          <div className="np-main-header">
            <div>
              <h1 className="np-main-title">
                {selectedNotice?.notice?.title || selectedNotice?.notice?.name || 'Select a notice'}
              </h1>
              {selectedNotice?.notice?.url && (
                <a
                  className="np-notice-link"
                  id="tour-view-notice"
                  href={selectedNotice.notice.url}
                  target="_blank"
                  rel="noreferrer"
                >
                  View notice ↗
                </a>
              )}

            </div>

            <div className="np-actions">
              <div className="np-chips-row">
                {filterRole && (
                  <span className="np-chip">
                    {filterRole}
                    <button
                      className="np-chip-close"
                      onClick={() => setFilterRole(null)}
                      aria-label={`Remove ${filterRole} filter`}
                    >×</button>
                  </span>
                )}
                {filterKey && (
                  <span className="np-chip">
                    {filterKey}
                    <button
                      className="np-chip-close"
                      onClick={() => setFilterKey(null)}
                      aria-label={`Remove ${filterKey} filter`}
                    >×</button>
                  </span>
                )}
              </div>

              <button
                className="np-btn np-btn--ghost"
                data-tour="data-breaches-btn"
                style={{ marginRight: 8 }}
                onClick={() => setShowBreachPage(true)}
              >
                🔔 Data Breaches
              </button>

              <button
                className="np-btn np-btn--ghost"
                data-tour="email-selected-btn"
                disabled={!selectedCount || sending}
                onClick={handleEmailSelected}
              >
                ✉ Email Selected{selectedCount > 0 ? ` (${selectedCount})` : ''}
              </button>

              <button
                className="np-btn np-btn--ghost"
                data-tour="clear-selection-btn"
                disabled={!selectedCount}
                onClick={() => setSelectedRows(new Set())}
              >
                Clear
              </button>

              <button
                className="np-btn np-btn--primary"
                data-tour="email-all-btn"
                disabled={sending || !filteredRows.length}
                onClick={handleEmailAll}
              >
                {sending ? '⏳ Sending…' : '⚡ Email All'}
              </button>
            </div>
          </div>

          {showBreachPage ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h2 style={{ margin: 0 }}>Data Breaches</h2>
                <div>
                  <button className="np-btn np-btn--ghost" onClick={() => setShowBreachPage(false)}>← Back</button>
                </div>
              </div>
              <div style={{ padding: 6, height: 'calc(100vh - 180px)', overflow: 'auto' }}>
                <DataBreachForm />
              </div>
            </>
          ) : selectedNotice ? (
            <>
              {/* Stat cards */}
              {/* <div className="np-stats-grid"> */}
              <div className="np-stats-grid" id="tour-stats-grid">
                {[
                  { key: 'total', label: 'Total' },
                  { key: 'accepted', label: 'Accepted' },
                  { key: 'declined', label: 'Declined' },
                  { key: 'revoked', label: 'Revoked' },
                ].map(({ key, label }) => (
                  <StatCard
                    key={key}
                    filterKey={key}
                    label={label}
                    value={statCounts[key] ?? 0}
                    total={statCounts.total}
                    activeKey={filterKey}
                    onFilter={handleFilterStatus}
                  />
                ))}
              </div>

              {/* Role cards */}
              {/* <div className="np-role-grid"> */}
              <div className="np-role-grid" id="tour-role-grid">
                {ROLES.map((role) => (
                  <RoleCard
                    key={role}
                    role={role}
                    stats={selectedNotice.roles?.[role] || emptyStats()}
                    isActive={filterRole === role}
                    onClick={() => handleFilterRole(role)}
                  />
                ))}
              </div>

              {/* Consent table */}
              <div className="np-table-section">
                <div className="np-table-toolbar">
                  <div>
                    <span className="np-table-title">
                      {selectedNotice.notice.title || 'Consents'}
                    </span>
                    <span className="np-table-count">
                      &nbsp;·&nbsp;<span title={Number(filteredRows.length).toLocaleString()}>{formatCompactNumber(filteredRows.length)} record{filteredRows.length !== 1 ? 's' : ''}</span>
                    </span>
                  </div>
                  <div className="np-table-actions">
                    {selectedCount > 0 && (
                      <span className="np-chip">{selectedCount} selected</span>
                    )}
                  </div>
                </div>

                {filteredRows.length === 0 ? (
                  <div className="np-table-empty">No matching consent records found.</div>
                ) : (
                  <div className="np-table-wrap">
                    <table className="np-table">
                      <thead>
                        <tr>
                          <th className="np-th np-th-check">
                            <input
                              type="checkbox"
                              className="np-checkbox"
                              data-tour="select-all-checkbox"
                              checked={!!allSelected}
                              onChange={handleToggleAll}
                              aria-label="Select all rows"
                            />
                          </th>
                          <th className="np-th">User</th>
                          <th className="np-th">Role</th>
                          <th className="np-th">Status</th>
                          <th className="np-th">Language</th>
                          <th className="np-th">Version</th>
                          <th className="np-th">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRows.map((r, idx) => {
                          const rowId = String(r.id || r.public_id || idx);
                          const isChecked = selectedRows.has(rowId);
                          const isPrev = previewRow
                            && String(previewRow.id || previewRow.public_id) === rowId;
                          const trCls = [
                            'np-tr',
                            isPrev ? 'np-tr--active' : '',
                            isChecked ? 'np-tr--selected' : '',
                          ].filter(Boolean).join(' ');

                          return (
                            <tr
                              key={rowId}
                              className={trCls}
                              data-tour="consent-row"
                              onClick={() => { setPreviewRow(r); setSendResult(null); }}
                            >
                              <td
                                className="np-td np-td-check"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <input
                                  type="checkbox"
                                  className="np-checkbox"
                                  data-tour="row-checkbox"
                                  checked={isChecked}
                                  onChange={() => handleToggleRow(rowId)}
                                  aria-label={`Select ${displayName(r)}`}
                                />
                              </td>
                              <td className="np-td">
                                <div className="np-user-cell">
                                  <div className="np-avatar" aria-hidden="true">
                                    {displayName(r)[0].toUpperCase()}
                                  </div>
                                  <span data-tour="user-name-link" style={{ textDecoration: 'underline', color: '#1e3a7a', cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); setUserDetailsRow(r); }}>{displayName(r)}</span>
                                </div>
                              </td>
                              <td className="np-td np-td-muted">
                                {normaliseRole(r) || 'Employee'}
                              </td>
                              <td className="np-td">
                                <StatusBadge status={normaliseStatus(r) || 'unknown'} />
                              </td>
                              <td className="np-td np-td-muted">
                                {displayLanguage(r)}
                              </td>
                              <td className="np-td np-td-mono">
                                {r.version || r.template_version || '—'}
                              </td>
                              <td className="np-td np-td-muted">
                                {formatDate(r.created_at || r.createdAt)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Preview panel */}
              <PreviewPanel
                row={previewRow}
                selectedNotice={selectedNotice}
                sending={sending}
                sendResult={sendResult}
                onSend={handleSingleSend}
                onClose={() => { setPreviewRow(null); setSendResult(null); }}
              />

              {/* User details modal shown when clicking the user email/name */}
              {userDetailsRow && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1300 }} onClick={() => setUserDetailsRow(null)}>
                  <div style={{ width: 540, maxWidth: '94vw', margin: '6vh auto', background: '#fff', borderRadius: 8, overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #eef2f7' }}>
                      <h3 style={{ margin: 0, fontSize: 16 }}>User Details</h3>
                      <button onClick={() => setUserDetailsRow(null)} style={{ border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer' }}>×</button>
                    </div>
                    <div style={{ padding: 16, maxHeight: '60vh', overflowY: 'auto' }}>
                      <div style={{ marginBottom: 8 }}><strong>Name:</strong> {userDetailsRow.name || userDetailsRow.username || '—'}</div>
                      <div style={{ marginBottom: 8 }}><strong>Email:</strong> {maskEmail(userDetailsRow.email) || '—'}</div>
                      <div style={{ marginBottom: 8 }}><strong>Phone:</strong> {maskPhone(userDetailsRow.phone) || '—'}</div>
                      <div style={{ marginBottom: 8 }}><strong>Role:</strong> {normaliseRole(userDetailsRow) || '—'}</div>
                      <div style={{ marginBottom: 8 }}><strong>Status:</strong> {normaliseStatus(userDetailsRow) || '—'}</div>
                      <div style={{ marginBottom: 8 }}><strong>Language:</strong> {displayLanguage(userDetailsRow)}</div>
                      <div style={{ marginBottom: 8 }}><strong>Version:</strong> {userDetailsRow.version || userDetailsRow.template_version || '—'}</div>
                      <div style={{ marginBottom: 8 }}><strong>Date:</strong> {formatDate(userDetailsRow.created_at || userDetailsRow.createdAt) || '—'}</div>
                      <div style={{ marginBottom: 8 }}><strong>Public ID:</strong> {userDetailsRow.public_id || userDetailsRow.id || '—'}</div>
                      {/* <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 13, color: '#475569', marginBottom: 6 }}>Consent</div>
                      <div style={{ background: '#fbfcfe', padding: 10, borderRadius: 6, maxHeight: '28vh', overflowY: 'auto', whiteSpace: 'pre-wrap' }}>{userDetailsRow.consent || userDetailsRow.consent_message || userDetailsRow.message || userDetailsRow.content || '—'}</div>
                  </div> */}
                      <div style={{ marginTop: 12 }}>
                        <div style={{ fontSize: 13, color: '#475569', marginBottom: 6 }}>
                          Consent Preview
                        </div>

                        <div
                          style={{
                            background: '#fbfcfe',
                            padding: 10,
                            borderRadius: 6,
                            maxHeight: '28vh',
                            overflowY: 'auto',
                            color: '#0f172a'
                          }}
                        >
                          {(userDetailsRow.consent ||
                            userDetailsRow.consent_message ||
                            userDetailsRow.message ||
                            userDetailsRow.content) ? (
                            <div
                              dangerouslySetInnerHTML={{
                                __html:
                                  userDetailsRow.consent ||
                                  userDetailsRow.consent_message ||
                                  userDetailsRow.message ||
                                  userDetailsRow.content
                              }}
                            />
                          ) : (
                            '—'
                          )}
                        </div>
                      </div>
                    </div>
                    <div style={{ padding: 12, display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: '1px solid #eef2f7' }}>
                      <button className="np-btn np-btn--ghost" onClick={() => setUserDetailsRow(null)}>Close</button>
                      <button className="np-btn np-btn--primary" data-tour="userdetails-send-btn" onClick={async () => {
                        try {
                          // send notice to this specific user
                          const rows = [userDetailsRow];
                          const result = await sendEmails(rows, selectedNotice);
                          try { window.toastSuccess(`Sent: ${result.success} · Failed: ${result.fail}`); } catch (e) { Swal.fire({ icon: 'success', title: 'Send Result', text: `Sent: ${result.success}  Failed: ${result.fail}`, customClass: { popup: 'swal-popup-custom' } }); }
                        } catch (err) {
                          console.error(err);
                          try { window.toastError('Failed to send to user'); } catch (e) { Swal.fire({ icon: 'error', title: 'Send Failed', text: 'Failed to send to user', customClass: { popup: 'swal-popup-custom' } }); }
                        } finally {
                          setUserDetailsRow(null);
                        }
                      }}>Send Email</button>
                    </div>
                  </div>
                </div>
              )}

              {/* Data Breaches modal */}
              {breachesOpen && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1300 }} onClick={() => setBreachesOpen(false)}>
                  <div style={{ width: 820, maxWidth: '96vw', margin: '4vh auto', background: '#fff', borderRadius: 8, overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #eef2f7' }}>
                      <h3 style={{ margin: 0, fontSize: 16 }}>Data Breach Templates</h3>
                      <button onClick={() => setBreachesOpen(false)} style={{ border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer' }}>×</button>
                    </div>
                    <div style={{ padding: 12, maxHeight: '60vh', overflowY: 'auto' }}>
                      {breachTemplates.length === 0 ? (
                        <div style={{ padding: 16, color: '#64748b' }}>No Data Breach templates found for this tenant.</div>
                      ) : (
                        breachTemplates.map((t, i) => (
                          <div key={i} style={{ border: '1px solid #eef2f7', padding: 12, borderRadius: 6, marginBottom: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div>
                                <div style={{ fontWeight: 600 }}>{t.title || t.consent_type || (t._source === 'audit' ? `Audit #${t.id}` : (t.username || t.email) || 'Data Breach')}</div>
                                <div style={{ fontSize: 12, color: '#64748b' }}>
                                  {t._source === 'notice' && (t.description || (t.consent_message || '').slice(0, 140))}
                                  {t._source === 'audit' && ((t.details || '').slice(0, 160))}
                                  {t._source === 'consent' && (`User: ${t.username || t.email || '—'} · Type: ${t.type || t.consent_type || '—'}`)}
                                </div>
                                <div style={{ marginTop: 6 }}>
                                  <small style={{ color: '#94a3b8' }}>{t._source === 'notice' ? 'Source: Privacy Notice' : t._source === 'audit' ? 'Source: Audit Log' : 'Source: Consent Record'}</small>
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: 8 }}>
                                {t._source === 'notice' && (
                                  <>
                                    <button className="np-btn np-btn--ghost" onClick={() => { navigator.clipboard?.writeText(t.html || t.content || t.consent_message || ''); }}>Copy HTML</button>
                                    <button className="np-btn np-btn--primary" onClick={async () => {
                                      try {
                                        const token = localStorage.getItem('token') || '';
                                        const headers = token ? { Authorization: `Bearer ${token}` } : {};
                                        const payload = {
                                          tenant_id: user?.tenant_id || Number(localStorage.getItem('tenant_id')) || null,
                                          title: t.title || (t.consent_type || 'Data Breach'),
                                          content: t.html || t.content || t.consent_message || '',
                                          description: t.description || t.consent_message || '',
                                          consent_type: (t.consent_type || 'data_breach'),
                                          consent_message: t.consent_message || t.content || t.html || '',
                                          html: t.html || t.content || t.consent_message || ''
                                        };
                                        await axios.post(`${API_BASE}/manager/privacy-notices`, payload, { headers });
                                        try { window.toastSuccess('Saved template to DB'); } catch (e) { Swal.fire({ icon: 'success', title: 'Saved', text: 'Saved template to DB', customClass: { popup: 'swal-popup-custom' } }); }
                                      } catch (err) {
                                        console.error('Failed to save template', err);
                                        try { window.toastError('Save failed: ' + (err?.response?.data?.detail || err.message || 'unknown')); } catch (e) { Swal.fire({ icon: 'error', title: 'Save Failed', text: 'Save failed: ' + (err?.response?.data?.detail || err.message || 'unknown'), customClass: { popup: 'swal-popup-custom' } }); }
                                      }
                                    }}>Save to DB</button>
                                  </>
                                )}
                                {t._source === 'audit' && (
                                  <button className="np-btn np-btn--ghost" onClick={() => { navigator.clipboard?.writeText(t.details || ''); }}>Copy Text</button>
                                )}
                                {t._source === 'consent' && (
                                  <>
                                    <button className="np-btn np-btn--ghost" onClick={() => { navigator.clipboard?.writeText((t.username || t.email || '') + ''); }}>Copy Identity</button>
                                    <button className="np-btn np-btn--primary" onClick={async () => {
                                      try {
                                        const token = localStorage.getItem('token') || '';
                                        const headers = token ? { Authorization: `Bearer ${token}` } : {};
                                        const payload = {
                                          tenant_id: user?.tenant_id || Number(localStorage.getItem('tenant_id')) || null,
                                          title: `NOTICE OF DATA BREACH - ${t.username || t.email || ''}`,
                                          content: t.html || t.content || t.description || t.consent_message || `Dear ${t.username || t.email},\nWe detected a security incident affecting your data on [date of breach].`,
                                          description: t.description || t.consent_message || '',
                                          consent_type: (t.type || t.consent_type || 'data_breach'),
                                          consent_message: t.consent_message || t.content || t.html || '',
                                          html: t.html || t.content || t.consent_message || ''
                                        };
                                        const res = await axios.post(`${API_BASE}/manager/privacy-notices`, payload, { headers });
                                        if (res && res.data && (res.data.success || res.status === 200)) {
                                          try { window.toastSuccess('Saved template to DB'); } catch (e) { Swal.fire({ icon: 'success', title: 'Saved', text: 'Saved template to DB', customClass: { popup: 'swal-popup-custom' } }); }
                                          // attempt to send immediately to the consent record's email using existing send flow
                                          try {
                                            const replacedHtml = applyPreviewPlaceholders(payload.html || payload.content || payload.consent_message || '', t);
                                            const savedNotice = { notice: { id: res.data.id || null, html: replacedHtml, content: replacedHtml, consent_message: payload.consent_message, title: payload.title } };
                                            const sendResult = await sendEmails([t], savedNotice);
                                            try { window.toastSuccess(`Send result — Sent: ${sendResult.success} · Failed: ${sendResult.fail}`); } catch (e) { Swal.fire({ icon: 'success', title: 'Send Result', text: `Send result — Sent: ${sendResult.success}  Failed: ${sendResult.fail}`, customClass: { popup: 'swal-popup-custom' } }); }
                                          } catch (sendErr) {
                                            console.error('Failed to send after save', sendErr);
                                            try { window.toastError('Saved but failed to send email: ' + (sendErr?.message || sendErr?.response?.data || 'unknown')); } catch (e) { Swal.fire({ icon: 'error', title: 'Send Failed', text: 'Saved but failed to send email: ' + (sendErr?.message || sendErr?.response?.data || 'unknown'), customClass: { popup: 'swal-popup-custom' } }); }
                                          }
                                        } else {
                                          throw new Error(res?.data?.detail || 'Save failed');
                                        }
                                      } catch (err) {
                                        console.error('Failed to save & send template', err);
                                        try { window.toastError('Save & send failed: ' + (err?.response?.data?.detail || err.message || 'unknown')); } catch (e) { Swal.fire({ icon: 'error', title: 'Save & Send Failed', text: 'Save & send failed: ' + (err?.response?.data?.detail || err.message || 'unknown'), customClass: { popup: 'swal-popup-custom' } }); }
                                      }
                                    }}>Save & Send</button>
                                  </>
                                )}
                              </div>
                            </div>
                            <div style={{ marginTop: 8 }}>
                              {t._source === 'notice' && (
                                <div dangerouslySetInnerHTML={{ __html: applyPreviewPlaceholders(t.html || t.content || t.consent_message || '', t).slice(0, 200) }} />
                              )}
                              {t._source === 'audit' && (
                                <div style={{ whiteSpace: 'pre-wrap', color: '#0f172a' }}>{applyPreviewPlaceholders(t.details || '', t)}</div>
                              )}
                              {t._source === 'consent' && (
                                <div style={{ color: '#0f172a' }}>
                                  Email: {t.email || '—'}<br />
                                  Username: {t.username || '—'}<br />
                                  Type: {t.type || t.consent_type || '—'}
                                  <div style={{ marginTop: 8, padding: 8, background: '#f8fafc', borderRadius: 6 }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Preview</div>
                                    <div style={{ color: '#0f172a' }} dangerouslySetInnerHTML={{ __html: applyPreviewPlaceholders(t.html || t.content || t.consent_message || `Dear {{name}},<br/>We detected a security incident on [date of breach].`, t) }} />
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="np-empty-state">
              <span className="np-empty-icon" aria-hidden="true">📋</span>
              <p>Select a notice from the sidebar to view consent statistics</p>
            </div>
          )}

        </main>
      </div>
      {guideOpen && (
        <GuideModal
          steps={GUIDE_STEPS}
          onClose={() => setGuideOpen(false)}
        />
      )}

    </div>
  );
}